import { useState, useEffect, useRef, useCallback } from 'react';
import API from '../lib/api';

import { useParams } from 'react-router';
import { Grid, GridCol, Container, Center, Paper, Code, Loader } from '@mantine/core';
import RecordingSVG from '../components/RecordingSVG';

// Format seconds to MM:SS timecode
function formatTimecode(seconds) {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export function RecordingPage() {
	const { recording_id } = useParams();
	const [r, setR] = useState(null);
	const [loading, setLoading] = useState(true);
	const [videoUrl, setVideoUrl] = useState(null);
	const [transcoding, setTranscoding] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const videoRef = useRef(null);

	// Update currentTime state on video timeupdate
	const handleTimeUpdate = useCallback(() => {
		if (videoRef.current) {
			setCurrentTime(videoRef.current.currentTime);
		}
	}, []);

	// Seek handler - called when clicking on detection IDs in the SVG
	const handleSeek = useCallback((time) => {
		if (!videoRef.current) return;
		videoRef.current.currentTime = time;
		if (videoRef.current.paused) {
			videoRef.current.play().catch(() => {});
		}
	}, []);

	// Fetch recording data
	useEffect(() => {
		const fetchRecording = async () => {
			const r = await API.post('recording/getRecording', { id: recording_id, getTracks: 1 });
			if (r.error) {
				console.error(r.error);
				return;
			}
			setR(r);
			setLoading(false);
		}
		fetchRecording();
	}, [recording_id]);

	// Get MP4 video URL (transcode if needed)
	useEffect(() => {
		if (!r) return;

		const getVideoUrl = async () => {
			// Check if it's a .ts file that needs transcoding
			const isTsFile = r.filename?.toLowerCase().endsWith('.ts');
			
			if (isTsFile) {
				setTranscoding(true);
				const result = await API.post('recording/getRecordingMp4', { id: recording_id });
				setTranscoding(false);
				
				if (result.error) {
					console.error('Transcode error:', result.error);
					// Fall back to original URL
					setVideoUrl(r.url);
				} else {
					setVideoUrl(result.url);
				}
			} else {
				// Use original URL for non-TS files
				setVideoUrl(r.url);
			}
		};

		getVideoUrl();
	}, [r, recording_id]);

	// Set up video element when URL is ready
	useEffect(() => {
		if (!videoUrl || !videoRef.current) return;
		
		const videoElement = videoRef.current;
		videoElement.src = videoUrl;
		videoElement.addEventListener('timeupdate', handleTimeUpdate);

		return () => {
			videoElement.removeEventListener('timeupdate', handleTimeUpdate);
		};
	}, [videoUrl, handleTimeUpdate]);

	// calculate % of the recording that is music from musicConfidence data - r.musicConfidence
	const calculateMusicPercentage = (recording) => {
		if (!recording || !recording.musicConfidence) return 0;
		const totalDuration = recording.musicConfidence.length;
		const musicDuration = recording.musicConfidence.reduce((acc, val) => acc + (val.confidence > 0.1 ? 1 : 0), 0);
		return (musicDuration / totalDuration) * 100;
	};

	
	// If the recording is still loading, say so
	if (loading) {
		return <h1>Loading...</h1>;
	}

	return (
		<>
			<Center>
				<Paper w={1000} shadow="sm" p="md" m="md">
					<Code>{r.filename}</Code><br/>
					{r.musicConfidence && <Code>Music Percentage: {calculateMusicPercentage(r).toFixed(2)}%</Code>}
					{/* <Grid mt="md">
						<GridCol span={6}>
							<h2>Watermark Matches</h2>
							{r.data?.matches && r.data.matches.map((match, i) => (
								<div key={match.start + '-' + match.mark}>
									{match.start} - {match.end}: <Code>{match.track?.raw?.file_id}</Code>
								</div>
							))}
						</GridCol>
						<GridCol span={6}>
							<h2>AFT Matches</h2>
							{r.fingerprints?.fpDetections && r.fingerprints.fpDetections.map((match, i) => (
								<div key={match.start + '-' + match.fileId} style={{ fontWeight: match.both ? 'bold' : 'normal' }}>
									{match.start} - {match.end}: <Code style={{ fontWeight: match.maxHit > 5 ? 'bold' : 'normal' }}>{match.fileId} - {match.maxHit}</Code>
								</div>
							))}
						</GridCol>
					</Grid> */}
					<Container mt="md">
						<RecordingSVG record={r} currentTime={currentTime} onSeek={handleSeek} />
					</Container>
				</Paper>
			</Center>

			{/* Fixed Video Player - Bottom Right */}
			{r && (
				<div style={{
					position: 'fixed',
					bottom: 20,
					right: 20,
					zIndex: 1000,
					background: '#1a1a1a',
					borderRadius: 8,
					padding: 12,
					boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
				}}>
					{/* Prominent Timecode Display */}
					<div style={{
						fontSize: '2.5rem',
						fontFamily: 'monospace',
						fontWeight: 'bold',
						color: '#fff',
						textAlign: 'center',
						marginBottom: 8,
						textShadow: '0 0 10px rgba(255, 255, 255, 0.3)'
					}}>
						{formatTimecode(currentTime)}
					</div>
					
					{/* Transcoding indicator */}
					{transcoding && (
						<div style={{
							width: 320,
							height: 180,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#000',
							borderRadius: 4,
							color: '#fff'
						}}>
							<Loader color="white" size="lg" />
							<div style={{ marginTop: 12 }}>Transcoding to MP4...</div>
						</div>
					)}
					
					{/* Video Player */}
					{!transcoding && (
						<video
							ref={videoRef}
							controls
							playsInline
							style={{
								width: 320,
								borderRadius: 4,
								display: 'block'
							}}
						/>
					)}
				</div>
			)}
		</>
	);
}
